// projects/domain/ach/tax-payments/tax-template-editable.entity.ts
import { AchFederalTaxTemplateModelDto, AchStateTaxTemplateModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../../shared';
import { TaxAmountLine, TaxCode, TaxType } from './types';

type EditableDto = AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto;

export class TaxTemplateEditable extends DomainEntity<EditableDto> {
    public static createNewDefault(type: TaxType): TaxTemplateEditable {
        const now = new Date().toISOString();
        const base = {
            id: 0,
            updatedDate: now,
            achPaymentTypeId: type,
            name: '',
            taxCode: '',
            taxPayerId: '',
            recipientName: '',
            recipientRoutingNumber: '',
            recipientAccountNumber: '',
            recipientAccountTypeId: 1,
            recipientAccountType: 'Checking',
            recipientIdNumber: '',
            isTemplate: true,
            isEdited: false,
            lastUpdated: now,
            frequency: {
                id: 0,
                updatedDate: now,
                effectiveDate: now,
                type: 'One Time',
                noEndDate: true,
                repeatOnDay1: 0,
                repeatOnDay2: 0,
            },
            applyUpdatesToBatch: false,
            offsetAccount: { id: 0, updatedDate: now, name: '' },
            payFromAccountId: 0,
            achCompanyName: ' ',
            achCompanyId: ' ',
            secCode: 'CCD',
            discretionaryData: ' ',
            entryDescription: ' ',
            restricted: false,
            debitAmount: 0,
            creditAmount: 0,
            status: 'Entered',
            errorMessage: ' ',
            successMessage: ' ',
            transactionId: ' ',
            batchId: 0,
            parentFrequency: {
                id: 0,
                updatedDate: now,
                type: 'One Time',
                noEndDate: true,
                repeatOnDay1: 0,
                repeatOnDay2: 0,
            },
            parentFrequencyType: ' ',
            parentId: 0,
            numberOfApprovalsNeeded: 0,
            completedApprovals: [],
            achPaymentTypeName: ' ',
            achPaymentTypeDescription: ' ',
            createdBy: ' ',
            createdDate: now,
            canReverseFull: false,
            canReverseTransactions: false,
            achPaymentDraftId: 0,
            hasAmountTypes: false,
            achCompany: {
                id: 0,
                updatedDate: now,
                companyName: ' ',
                companyId: ' ',
                companyGuidUniqueId: ' ',
                entryDescription: ' ',
                discretionaryData: ' ',
                totalDailyLimit: 0,
                notOnUsTransactionTypes: ' ',
                batchBalanceRequirements: ' ',
                onUsTransactionsRequired: 0,
                onUsAmountRequiredPerBatch: 0,
                isActive: false,
                allowUnbalancedPayments: false,
                canReverseFull: false,
                canReverseTransactions: false,
            },
            recipients: [
                {
                    id: 0,
                    updatedDate: now,
                    name: ' ',
                    idNumber: ' ',
                    accountNumber: ' ',
                    accountType: 'Checking',
                    routingNumber: ' ',
                    transactionType: 'CR',
                    amount: 0,
                    prenote: false,
                    hold: false,
                    reversal: false,
                    addendaType: ' ',
                    addendaBody: ' ',
                    errorMessage: ' ',
                    isRoutingNumberValid: false,
                    isRoutingNumberOnUs: false,
                    isDeleted: false,
                    default: true,
                },
            ],
        };

        if (type === TaxType.Federal) {
            return new TaxTemplateEditable({
                ...base,
                achFederalTaxPaymentAmounts: [
                    {
                        id: 0,
                        achFederalAmountTypeId: 0,
                        achBatchId: 0,
                        amountType: '',
                        amount: 0,
                        updatedDate: now,
                        achPaymentId: 0,
                        createdDate: now,
                        hasAmountTypes: false,
                    },
                ],
                achStateTaxPaymentAmounts: [
                    {
                        id: 0,
                        achStateAmountTypeId: 1,
                        achStateAmountTypeName: ' ',
                        achStateAmountTypeDescription: ' ',
                        amountTypeName: ' ',
                        amountTypeDescription: ' ',
                        amount: 0,
                        updatedDate: now,
                        achPaymentId: 0,
                        createdDate: now,
                        createdBy: ' ',
                    },
                ],
            } as unknown as EditableDto);
        }
        return new TaxTemplateEditable({
            ...base,
            stateId: 0,
            state: '',
            achStateTaxPaymentAmounts: [
                {
                    id: 0,
                    achStateAmountTypeId: 1,
                    achBatchId: 0,
                    achStateAmountTypeName: ' ',
                    achStateAmountTypeDescription: ' ',
                    amountTypeName: ' ',
                    amountTypeDescription: ' ',
                    amount: 0,
                    updatedDate: now,
                    achPaymentId: 0,
                    createdDate: now,
                    createdBy: ' ',
                },
            ],
            achFederalTaxPaymentAmounts: [],
        } as unknown as EditableDto);
    }

    private _selectedTaxCode?: TaxCode;

    private _selectedState?: { key: string; value: string; id: number };

    public createDefault(): this {
        return TaxTemplateEditable.createNewDefault(TaxType.Federal) as this;
    }

    public set selectedTaxCode(code: TaxCode) {
        this._selectedTaxCode = code;
    }

    public set selectedState(state: { key: string; value: string; id: number }) {
        this._selectedState = state;
    }

    public get isFederal(): boolean {
        return (
            (this.dto as unknown as { achPaymentTypeId?: number }).achPaymentTypeId ===
            TaxType.Federal
        );
    }

    public get isState(): boolean {
        return (
            (this.dto as unknown as { achPaymentTypeId?: number }).achPaymentTypeId ===
            TaxType.State
        );
    }

    public get taxCode(): string {
        return this.dto.taxCode ?? '';
    }

    public set taxCode(value: string) {
        this.dto.taxCode = value;
    }

    public get taxPayerId(): string {
        return this.dto.taxPayerId ?? '';
    }

    public set taxPayerId(value: string) {
        this.dto.taxPayerId = value;
    }

    public get recipientName(): string {
        return this.dto.recipientName ?? '';
    }

    public set recipientName(value: string) {
        this.dto.recipientName = value;
    }

    public get name(): string {
        return this.dto.name ?? '';
    }

    public set name(value: string) {
        this.dto.name = value;
    }

    public setAchCompany(companyDto: Record<string, unknown>) {
        const dto = this.dto as unknown as Record<string, unknown>;
        dto.achCompany = companyDto;
        dto.achCompanyId = companyDto.companyId;
        dto.achCompanyName = companyDto.companyName;
        const secCodes = companyDto.secCodes as { code?: string }[] | undefined;
        dto.secCode = secCodes?.[0]?.code ?? 'CCD';
        dto.discretionaryData = companyDto.discretionaryData ?? '';
        dto.entryDescription = companyDto.entryDescription ?? '';
    }

    public get recipientRoutingNumber(): string {
        return this.dto.recipientRoutingNumber ?? '';
    }

    public set recipientRoutingNumber(value: string) {
        this.dto.recipientRoutingNumber = value;
    }

    public get recipientAccountNumber(): string {
        return this.dto.recipientAccountNumber ?? '';
    }

    public set recipientAccountNumber(value: string) {
        this.dto.recipientAccountNumber = value;
    }

    public get recipientAccountType(): string {
        return this.dto.recipientAccountType ?? '';
    }

    public get recipientIdNumber(): string {
        return this.dto.recipientIdNumber ?? '';
    }

    public set recipientIdNumber(value: string) {
        this.dto.recipientIdNumber = value;
    }

    public get recipientAccountTypeId(): number {
        return this.dto.recipientAccountTypeId ?? 0;
    }

    public set recipientAccountTypeId(value: number) {
        this.dto.recipientAccountTypeId = value;
    }

    public get stateId(): number | undefined {
        return this.isState ? (this.dto as AchStateTaxTemplateModelDto).stateId : undefined;
    }

    public get state(): string | undefined {
        return this.isState ? (this.dto as AchStateTaxTemplateModelDto).state : undefined;
    }

    public get amounts(): TaxAmountLine[] {
        if (this.isFederal) {
            const fedDto = this.dto as AchFederalTaxTemplateModelDto;
            return (fedDto.achFederalTaxPaymentAmounts ?? []).map(a => ({
                amount: a.amount ?? 0,
                amountTypeId: a.achFederalAmountTypeId ?? 0,
                amountTypeName: a.achFederalAmountTypeName ?? '',
                amountTypeDescription: a.achFederalAmountTypeDescription ?? '',
            }));
        }
        const stateDto = this.dto as AchStateTaxTemplateModelDto;
        return (stateDto.achStateTaxPaymentAmounts ?? []).map(a => ({
            amount: a.amount ?? 0,
            amountTypeId: a.achStateAmountTypeId ?? 0,
            amountTypeName: a.achStateAmountTypeName ?? '',
            amountTypeDescription: a.achStateAmountTypeDescription ?? '',
        }));
    }

    public setAmounts(amounts: TaxAmountLine[]): void {
        const now = new Date().toISOString();
        if (this.isFederal) {
            const fedDto = this.dto as AchFederalTaxTemplateModelDto;
            fedDto.achFederalTaxPaymentAmounts = amounts.map(a => ({
                id: 0,
                achFederalAmountTypeId: a.amountTypeId,
                achFederalAmountTypeName: a.amountTypeName,
                achFederalAmountTypeDescription: a.amountTypeDescription,
                achBatchId: 0,
                amountType: a.amountTypeName,
                amount: a.amount,
                updatedDate: now,
                achPaymentId: 0,
                createdDate: now,
                hasAmountTypes: a.amountTypeId > 0,
            }));
        } else {
            const stateDto = this.dto as AchStateTaxTemplateModelDto;
            stateDto.achStateTaxPaymentAmounts = amounts.map(a => ({
                id: 0,
                achStateAmountTypeId: a.amountTypeId,
                achBatchId: 0,
                achStateAmountTypeName: a.amountTypeName || ' ',
                achStateAmountTypeDescription: a.amountTypeDescription || ' ',
                amount: a.amount,
                updatedDate: now,
                achPaymentId: 0,
                createdDate: now,
                createdBy: ' ',
            }));
        }
    }

    public toDto(): EditableDto {
        return this.dto;
    }

    public toSubmitDto(): Record<string, unknown> {
        const dto = this.dto as unknown as Record<string, unknown>;
        const achCompany = dto.achCompany as Record<string, unknown> | undefined;

        const fedAmounts = (
            (dto.achFederalTaxPaymentAmounts as Array<Record<string, unknown>>) ?? []
        ).map(a => ({
            achFederalAmountTypeId: a.achFederalAmountTypeId ?? undefined,
            amountType: a.amountType ?? a.achFederalAmountTypeName ?? '',
            amount: a.amount ?? 0,
        }));

        const stateAmounts = (
            (dto.achStateTaxPaymentAmounts as Array<Record<string, unknown>>) ?? []
        ).map(a => ({
            achStateAmountTypeId: a.achStateAmountTypeId ?? 1,
            amount: a.amount ?? 0,
        }));

        const base: Record<string, unknown> = {
            name: dto.name,
            achCompany: achCompany,
            achCompanyName: dto.achCompanyName,
            achCompanyId: dto.achCompanyId,
            secCode: dto.secCode,
            discretionaryData: (dto.discretionaryData as string)?.trim() || undefined,
            entryDescription: (dto.entryDescription as string)?.trim() || undefined,
            restricted: dto.restricted ?? false,
            recipients: [
                {
                    name: undefined,
                    idNumber: undefined,
                    accountNumber: undefined,
                    accountType: 'Checking',
                    routingNumber: undefined,
                    isRoutingNumberValid: false,
                    isRoutingNumberOnUs: false,
                    transactionType: 'CR',
                    amount: 0,
                    addendaType: undefined,
                    addendaBody: undefined,
                    prenote: false,
                    hold: false,
                    isDeleted: false,
                    default: true,
                },
            ],
            lastUpdated: dto.lastUpdated,
            numberOfApprovalsNeeded: dto.numberOfApprovalsNeeded ?? 0,
            completedApprovals: [],
            status: '',
            isTemplate: true,
            recipientAccountTypeId: dto.recipientAccountTypeId,
            achStateTaxPaymentAmounts: stateAmounts,
            achPaymentTypeId: dto.achPaymentTypeId,
            taxCode: dto.taxCode,
            achFederalTaxPaymentAmounts: fedAmounts,
            offsetAccount: undefined,
            recipientName: dto.recipientName,
            recipientRoutingNumber: dto.recipientRoutingNumber,
            recipientAccountNumber: dto.recipientAccountNumber,
            recipientIdNumber: (dto.recipientIdNumber as string) || undefined,
            taxPayerId: dto.taxPayerId,
            payFromAccountId: undefined,
            ...(this.isFederal
                ? { selectedFederalTaxCode: this._selectedTaxCode }
                : {
                      stateId: (dto as unknown as { stateId?: number }).stateId,
                      selectedState: this._selectedState,
                      selectedStateTaxCode: this._selectedTaxCode,
                  }),
        };

        return base;
    }
}
